import {useTranslation} from 'react-i18next';
import useOpenState from '../../../../hooks/useOpenState';
import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {user} from '../../../../store/reducers/user';
import Formatter from '../../../../util/formatter';
import moment from 'moment/moment';
import uuidv4 from 'uuid/v4';
import Notes from '../../../../util/notes';
import {Stack} from '@mui/material';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import AgyleBlockSwitch from '../../../../components/ui/switch/agyleBlockSwitch';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import * as _ from 'lodash';
import MessageBox from '../../../../util/messageBox';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import {usePlansStyles} from './plansStyle';
import MiniFormDialog from '../../../../components/modal/miniFormDialog';

const initialNonContactFormData = () => {
    return {
        airway: {
            value: null,
            comment: '',
            checkedBy: '',
            checkedDate: null,
        },
        breathing: {
            value: null,
            comment: '',
            checkedBy: '',
            checkedDate: null,
        },
        circulation: {
            value: null,
            comment: '',
            checkedBy: '',
            checkedDate: null,
        },
        disability: {
            value: null,
            comment: '',
            checkedBy: '',
            checkedDate: null,
        },
        exposure: {
            value: null,
            comment: '',
            checkedBy: '',
            checkedDate: null,
        },
    };
};

const shortName = {
    airway: {
        key: 'A',
        description: 'Airway',
    },
    breathing: {
        key: 'B',
        description: 'Breathing',
    },
    circulation: {
        key: 'C',
        description: 'Circulation',
    },
    disability: {
        key: 'D',
        description: 'Disability',
    },
    exposure: {
        key: 'E',
        description:
            'Exposure',
    },
};

const NonContactObsFormDialog = (props) => {
    const classes = usePlansStyles();
    const {t} = useTranslation();

    const [isCommentDialogOpen, openCommentDialog, closeCommentDialog] = useOpenState(false, true);

    const [formState, setFormState] = useState(() => initialNonContactFormData());

    const [keyOfComment, setKeyOfComment] = useState(null);
    const [commentState, setCommentState] = useState('');

    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);

    const handleOpenCommentDialog = (key) => {
        setKeyOfComment(key);
        openCommentDialog();
    };

    const handleCloseCommentDialog = () => {
        setKeyOfComment(null);
        setCommentState('');
        closeCommentDialog();
    };

    const handleSaveCommentDialog = () => {
        setFormState((prevState) => ({
            ...prevState,
            [keyOfComment]: {
                ...prevState[keyOfComment],
                comment: commentState,
            },
        }));
        setCommentState('');
        closeCommentDialog();
        setKeyOfComment(null);
    };

    const setFormAndCloseDialog = () => {
        const objToSave = {
            ...formState,
            date: moment(),
            by: tester,
            id: uuidv4(),
        };

        const dataToSave = [...props.obsData, objToSave];

        props.setObsData(dataToSave);

        const data = [
            {
                name: 'studies.nonContactObs',
                value: dataToSave,
                noteToAdd: Notes.getNonContactObsNote(formState),
            },
        ];

        props.onChange(data);
        props.onClose();
        // props.handleCloseNonContact();
        setFormState(initialNonContactFormData);
    };

    useEffect(() => {
        if (keyOfComment) {
            setCommentState(formState[keyOfComment].comment);
        } else {
            setCommentState('');
        }
    }, [keyOfComment]);

    const renderInputOptions = (key, property) => {
        const mapCurrentValue = (value) => {
            if (value === 'yes') {
                return true;
            } else if (value === 'no') {
                return false;
            } else {
                return null;
            }
        };
        return (
            <Stack
                spacing={2}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{mt: 1, p: 1, flexWrap: 'wrap'}}
                className={classes.switchRow}
                key={key}
            >
                <Grid
                    item
                    xs={12}
                    md={6}
                    className={classes.descriptionBox}
                >
                    <Typography
                        variant="h6"
                        component="div"
                        style={{
                            borderRadius: '10px',
                            minHeight: '64px',
                            minWidth: '270px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {shortName[key]?.description}
                    </Typography>
                </Grid>

                <Grid item xs={12} md={5}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="flex-end"
                        gap={1}
                        sx={{p: 1}}
                    >
                        <div className={classes.centerRow}>
                            <AgyleBlockSwitch
                                onChange={e => {
                                    const value = e === true ? 'yes' : e === false ? 'no' : null;
                                    setFormState({
                                        ...formState,
                                        [key]: {
                                            ...formState[key],
                                            value: value,
                                            checkedBy: tester,
                                            checkedDate: moment(),
                                        },
                                    });
                                }}
                                value={mapCurrentValue(property.value)}
                                nullState={true}
                                style={{marginRight: 10}}
                                customLabels={{
                                    yes: t('NAD'),
                                    no: t('Abnormality'),
                                }}
                            />
                        </div>

                        <Grid item xs={4} sm={3} md={4} style={{flexBasis: 'content'}}>
                            <AgyleButton
                                variant="contained"
                                size="large"
                                onClick={() => handleOpenCommentDialog(key)}
                            >
                                {t(' + Comment')}
                            </AgyleButton>
                        </Grid>
                    </Stack>
                </Grid>

                {property.comment &&
                    <Grid item xs={12} sx={{pt: 1.5}}>
                        <Stack spacing={2} direction="row" alignItems="center">
                            <Typography variant="body1" component="div">
                                Comment:
                            </Typography>
                            <Typography
                                variant="body2"
                                component="div"
                                // gutterBottom
                                // color="black"
                            >
                                {property.comment}
                            </Typography>
                        </Stack>
                    </Grid>
                }
            </Stack>
        );
    };

    const addCommentObj = () => {
        return (
            <AgyleTextField
                id="name"
                label={t('Comment')}
                type="text"
                fullWidth
                multiline
                value={commentState}
                onChange={(e) => {
                    setCommentState(e.target.value);
                }}
                debounce
                noBorder={false}
                withNewBackground
            />
        );
    };

    return (
        <>
            <MiniFormDialog
                open={props.open}
                onClose={props.onClose}
                title={t('NON - Contact Physical Health Observations Tool Form')}
                width="md"
                onSave={setFormAndCloseDialog}
                disableSubmit={props.disableSave}
            >
                <>
                    {!_.isEmpty(formState) &&
                        _.map(formState, (property, key) => {
                            return renderInputOptions(key, property);
                        })}
                </>
            </MiniFormDialog>

            <MessageBox
                open={isCommentDialogOpen}
                onClose={handleCloseCommentDialog}
                title={t('Add Comment')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={handleSaveCommentDialog}
                messageObject={addCommentObj()}
                fullWidth
            />
        </>
    );
};

export default NonContactObsFormDialog;
